"use client"

import React, { useCallback, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { CalendarPlus, Loader2, X, AlertTriangle, Info } from "lucide-react"
import { Button } from "../../ui/button"
import { appointmentApi } from "../../../services/appointmentApi"
import type { BatchBookingCandidate } from "../../../services/appointmentApi"
import { containerKindLabel } from "../../../utils/containerTypeLabel"

/**
 * 批量预约弹窗。
 *
 * 调度在派单列表里点某只 released 柜上的日历图标 → 这里列出**能和它一起约的
 * 所有柜**，勾选后一次送进 eModal。
 *
 * 「能一起约」不是我们定的规矩，是 eModal 向导的形状：第 1 步 Terminal 和
 * Move Type 都是单选，所以一个向导里只能装同码头、同动作的柜。列表因此天然
 * 就是同质的，界面上不需要再让人去挑码头。
 *
 * 缺东西约不了的柜（比如 ETS 要 PIN，而这张订单没有提单号）**照样列出来**，
 * 只是勾选框禁用并写明原因。藏起来的话调度只会想「我那只柜怎么不见了」，
 * 然后去手工约 —— 而真正的问题（订单缺提单号）没人知道。
 */

interface BatchBookingModalProps {
  open: boolean
  onClose: () => void
  /** 调度点进来的那只柜。列表从它出发。 */
  anchorTaskId: number | null
  /** 勾好了、点 Book。返回 false 表示没能交给插件，弹窗留在原地。 */
  onBook: (taskIds: number[]) => Promise<boolean>
}

export function BatchBookingModal({ open, onClose, anchorTaskId, onBook }: BatchBookingModalProps) {
  const [loading, setLoading] = useState(false)
  const [booking, setBooking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [candidates, setCandidates] = useState<BatchBookingCandidate[]>([])
  const [terminalName, setTerminalName] = useState<string>("")
  const [moveType, setMoveType] = useState<string>("")
  const [maxPerBatch, setMaxPerBatch] = useState<number>(10)
  // 出口在码头认订舱号，进口认柜号。列表那一列的表头和底下那句说明都跟着它变。
  const [identity, setIdentity] = useState<'container' | 'booking'>('container')
  // 出口是「一张票挂 N 个 move」，码头对一张票卡两条：最多 4 个、尺寸加起来 ≤80 呎。
  // 两条都要在勾选这一步就拦住，不然人填满了才被码头顶回来。
  const [maxTotalFeet, setMaxTotalFeet] = useState<number | null>(null)
  const [truncated, setTruncated] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(new Set())

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !booking) onClose()
  }, [booking, onClose])

  useEffect(() => {
    if (!open) return
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, handleKeyDown])

  useEffect(() => {
    if (!open || !anchorTaskId) return
    let cancelled = false

    setLoading(true)
    setError(null)
    setCandidates([])
    setSelected(new Set())

    appointmentApi.getBatchCandidates(anchorTaskId)
      .then((res) => {
        if (cancelled) return
        if ('notSupported' in res) {
          setError(res.message)
          return
        }
        setCandidates(res.candidates)
        setTerminalName(res.terminalName || res.terminalCode)
        setMoveType(res.moveType)
        setIdentity(res.identity === 'booking' ? 'booking' : 'container')
        setMaxTotalFeet(res.maxTotalFeet ?? null)
        setMaxPerBatch(res.maxPerBatch)
        setTruncated(res.truncated)
        // 调度是点着这只柜进来的，默认只勾它。其余的要不要一起约由人决定 ——
        // 全勾上会让「点错一只柜」的代价变成「多约了九张票」。
        const anchor = res.candidates.find((c) => c.isAnchor && !c.blocked)
        setSelected(anchor ? new Set([anchor.taskId]) : new Set())
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message || 'Failed to load containers')
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [open, anchorTaskId])

  if (!open) return null

  const bookable = candidates.filter((c) => !c.blocked)
  const feetOf = (c: BatchBookingCandidate) => c.sizeFeet ?? 0
  const selectedFeet = candidates
    .filter((c) => selected.has(c.taskId))
    .reduce((sum, c) => sum + feetOf(c), 0)
  const atLimit = selected.size >= maxPerBatch
  const atFeetLimit = maxTotalFeet !== null && selectedFeet >= maxTotalFeet

  /** 再加这一只会不会超出码头对一张票的两条限制。 */
  const wouldExceed = (c: BatchBookingCandidate) => {
    if (selected.size >= maxPerBatch) return true
    if (maxTotalFeet === null) return false
    return selectedFeet + feetOf(c) > maxTotalFeet
  }

  const toggle = (c: BatchBookingCandidate) => {
    if (c.blocked) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(c.taskId)) next.delete(c.taskId)
      else if (!wouldExceed(c)) next.add(c.taskId)
      return next
    })
  }

  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size > 0) return new Set()
      // 一张票装不下全部时，按列表顺序能装几只装几只（个数和呎数两条都看）。
      const picked = new Set<number>()
      let feet = 0
      for (const c of bookable) {
        if (picked.size >= maxPerBatch) break
        if (maxTotalFeet !== null && feet + feetOf(c) > maxTotalFeet) continue
        picked.add(c.taskId)
        feet += feetOf(c)
      }
      return picked
    })
  }

  const handleBook = async () => {
    if (selected.size === 0 || booking) return
    setBooking(true)
    setError(null)
    try {
      // 顺序按列表显示的顺序，锚点柜排第一 —— 后端把第一只柜当作这次会话的
      // 主任务，弹窗上看到的次序和 eModal 里、和事后审计里是同一个次序。
      const ordered = candidates.filter((c) => selected.has(c.taskId)).map((c) => c.taskId)
      const ok = await onBook(ordered)
      if (ok) onClose()
      else setError('Could not hand this booking to the EAloom extension.')
    } catch (e) {
      setError((e as Error).message || 'Failed to start booking')
    } finally {
      setBooking(false)
    }
  }

  const modalContent = (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={() => { if (!booking) onClose() }} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <CalendarPlus className="h-5 w-5 text-emerald-600 flex-shrink-0" />
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 leading-tight">Book terminal appointment</h3>
                {terminalName && (
                  <p className="text-xs text-gray-500 truncate">
                    {terminalName}
                    {moveType ? ` · ${moveType.replace(/_/g, ' ')}` : ''}
                  </p>
                )}
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} disabled={booking}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                <span className="ml-2 text-sm text-gray-500">Loading containers…</span>
              </div>
            ) : error && candidates.length === 0 ? (
              <div className="p-6">
                <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-900">{error}</p>
                </div>
              </div>
            ) : (
              <>
                <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center gap-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                    checked={bookable.length > 0 && selected.size > 0}
                    onChange={toggleAll}
                    aria-label="Select all containers"
                  />
                  <span className="flex-1">{identity === 'booking' ? 'Booking' : 'Container'}</span>
                  <span className="w-40">Client</span>
                  <span className="w-32">Terminal</span>
                </div>

                <ul className="divide-y divide-gray-100">
                  {candidates.map((c) => {
                    const isSelected = selected.has(c.taskId)
                    const disabled = c.blocked || (!isSelected && wouldExceed(c))
                    return (
                      <li
                        key={c.taskId}
                        onClick={() => !disabled && toggle(c)}
                        className={
                          'px-4 py-2.5 flex items-center gap-3 text-sm '
                          + (disabled ? 'opacity-50 cursor-not-allowed ' : 'cursor-pointer hover:bg-emerald-50/50 ')
                          + (isSelected ? 'bg-emerald-50/70' : '')
                        }
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          checked={isSelected}
                          disabled={disabled}
                          onChange={() => toggle(c)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select ${c.containerNumber || c.bookingNumber || c.taskNumber}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {/* 出口柜这会儿没有柜号 —— 屏幕上唯一认得出「这是哪一票」的
                                是订舱号（和派单板上 BKG 那一行同一个规矩）。 */}
                            <span className="font-semibold text-gray-900">
                              {c.containerNumber || c.bookingNumber || '—'}
                            </span>
                            {!c.containerNumber && c.bookingNumber && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                                BKG
                              </span>
                            )}
                            {c.containerSize && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                                {c.containerSize}
                              </span>
                            )}
                            {/* 冷柜要插电、码头按柜型分预约窗口 —— 一批里混进一只
                                Reefer 是调度必须当场看见的事，所以柜号旁边一定有柜型。
                                判不出来就不画这一枚：这里是「确认要一起约的是这几只」
                                的最后一眼，标错比不标更糟。判法见 containerTypeLabel，
                                和派单列表同一个规矩。 */}
                            {containerKindLabel(c.containerSize, c.containerType) && (
                              <span
                                className={
                                  'text-[10px] px-1.5 py-0.5 rounded '
                                  + (containerKindLabel(c.containerSize, c.containerType) === 'Reefer'
                                    ? 'bg-blue-100 text-blue-700 font-semibold'
                                    : 'bg-gray-100 text-gray-500')
                                }
                              >
                                {containerKindLabel(c.containerSize, c.containerType)}
                              </span>
                            )}
                            {c.isAnchor && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                                this row
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 truncate">{c.taskNumber}</div>
                          {c.blocked && c.blockedReason && (
                            <div className="text-xs text-amber-700 mt-0.5">{c.blockedReason}</div>
                          )}
                        </div>
                        <span className="w-40 truncate text-gray-600">{c.clientName || '—'}</span>
                        <span className="w-32 truncate text-gray-600">{c.terminalCode}</span>
                      </li>
                    )
                  })}
                </ul>

                {candidates.length === 0 && (
                  <p className="px-4 py-10 text-center text-sm text-gray-400">
                    No released containers are waiting for an appointment at this terminal.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4 flex-shrink-0 space-y-3">
            {/* 一票一趟车。话只说这一句 —— 装得下多少，调度一眼就懂；
                解释 twin 是什么、为什么，都是这一行放不下也不必放的东西。
                用码头自己的说法（twin / ft），和 eModal 上看到的是同一套词。 */}
            {identity === 'booking' && (
              <p className="flex items-center gap-2 text-xs text-gray-500">
                <Info className="h-3.5 w-3.5 flex-shrink-0" />
                One appointment = one truck run: 2 × 20 ft (twin) or 1 × 40 ft
              </p>
            )}
            {truncated && (
              <p className="flex items-start gap-2 text-xs text-gray-500">
                <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                Only the containers closest to their last free day are listed.
              </p>
            )}
            {error && candidates.length > 0 && (
              <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-900">{error}</p>
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                {selected.size} selected
                {maxTotalFeet !== null && ` · ${selectedFeet}/${maxTotalFeet} ft`}
                {(atLimit || atFeetLimit) && ' · full'}
                {selected.size > 1 && identity !== 'booking'
                  && ' · you will pick a time slot for each one in eModal'}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={onClose} disabled={booking}>Cancel</Button>
                <Button
                  onClick={handleBook}
                  disabled={selected.size === 0 || booking}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {booking && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {selected.size > 1 ? `Book ${selected.size} containers` : 'Book'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )

  return createPortal(modalContent, document.body)
}

export default BatchBookingModal
